// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useState, type FormEvent } from 'react';
import { Button, Card, Input, Label, Stack } from '@intakeform/ui';
import {
  useDeleteEmrConnection,
  useEmrConnection,
  useUpsertEmrConnection,
  type EmrConnectionView,
} from '../../api/emr-connection.js';

type AdapterKind = EmrConnectionView['adapterKind'];

const ADAPTER_KINDS: { value: AdapterKind; label: string; needsCreds: boolean }[] = [
  { value: 'fixture', label: 'Fixture (dev / no real EMR)', needsCreds: false },
  { value: 'generic-fhir', label: 'Generic FHIR (via gateway)', needsCreds: true },
  { value: 'epic', label: 'Epic (via gateway)', needsCreds: true },
];

/**
 * Admin-only settings panel for the org's EMR connection (bead 8de).
 * Lets a clinic register their own adapter kind + gateway URL +
 * credentials. The credentials field is write-only: GET responses
 * only report `hasCredentials: boolean`. Clearing the connection
 * via "Disconnect" deletes the row so the resolver falls back to
 * the process-wide default.
 */
export function EmrConnectionPanel() {
  const { data, error: loadError } = useEmrConnection();
  const upsert = useUpsertEmrConnection();
  const remove = useDeleteEmrConnection();

  const [kind, setKind] = useState<AdapterKind>('fixture');
  const [displayName, setDisplayName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [gatewayToken, setGatewayToken] = useState('');

  useEffect(() => {
    if (data?.connection === null || data?.connection === undefined) return;
    setKind(data.connection.adapterKind);
    setDisplayName(data.connection.displayName ?? '');
    setBaseUrl(data.connection.baseUrl ?? '');
    // Never preload the gateway token — even if the API returned it,
    // showing it in the input would defeat the write-only model.
    setGatewayToken('');
  }, [data]);

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const body: Parameters<typeof upsert.mutate>[0] = {
      adapterKind: kind,
      displayName: displayName.trim().length > 0 ? displayName.trim() : null,
      baseUrl: baseUrl.trim().length > 0 ? baseUrl.trim() : null,
    };
    if (gatewayToken.trim().length > 0) {
      body.credentials = { gatewayToken: gatewayToken.trim() };
    }
    upsert.mutate(body, {
      onSuccess: () => {
        setGatewayToken('');
      },
    });
  };

  const onDisconnect = (): void => {
    remove.mutate(undefined, {
      onSuccess: () => {
        setKind('fixture');
        setDisplayName('');
        setBaseUrl('');
        setGatewayToken('');
      },
    });
  };

  const current = data?.connection;
  // ADAPTER_KINDS always has the 'fixture' entry, so `.find` returning
  // undefined would mean a runtime invariant has broken — fall back to
  // a synthetic default rather than non-null-asserting an array index.
  const kindInfo = ADAPTER_KINDS.find((k) => k.value === kind) ?? {
    value: 'fixture' as const,
    label: '',
    needsCreds: false,
  };
  const credentialsNeeded = kindInfo.needsCreds;
  const credentialsExist = current !== null && current?.hasCredentials === true;

  return (
    <Card heading="EMR connection" variant="default" data-emr-connection-panel>
      <p className="text-sm text-muted-foreground" style={{ marginBottom: 12 }}>
        Configure the EMR endpoint and credentials this clinic uses for autofill and (future)
        coverage verification. Credentials are encrypted at rest with the server's master key.
      </p>

      {loadError !== null && loadError !== undefined ? (
        <div className="error-banner text-sm" role="alert" style={{ marginBottom: 12 }}>
          {loadError.message}
        </div>
      ) : null}

      <form onSubmit={onSubmit} style={{ maxWidth: 560 }}>
        <Stack gap={16}>
          <Stack gap={4}>
            <Label htmlFor="emr-kind">Adapter</Label>
            <select
              id="emr-kind"
              data-emr-kind
              value={kind}
              onChange={(e) => setKind(e.currentTarget.value as AdapterKind)}
              className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {ADAPTER_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </Stack>

          <Stack gap={4}>
            <Label htmlFor="emr-display-name">Display name (optional)</Label>
            <Input
              id="emr-display-name"
              type="text"
              value={displayName}
              data-emr-display-name
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              placeholder="Cerner Prod, Epic Sandbox, etc."
            />
          </Stack>

          {credentialsNeeded ? (
            <>
              <Stack gap={4}>
                <Label htmlFor="emr-base-url" required>
                  Gateway base URL
                </Label>
                <Input
                  id="emr-base-url"
                  type="url"
                  required
                  value={baseUrl}
                  data-emr-base-url
                  onChange={(e) => setBaseUrl(e.currentTarget.value)}
                  placeholder="https://gateway.your-clinic.example.com"
                />
              </Stack>
              <Stack gap={4}>
                <Label htmlFor="emr-token" required={!credentialsExist}>
                  {credentialsExist ? 'Rotate credentials (optional)' : 'Gateway token'}
                </Label>
                <Input
                  id="emr-token"
                  type="password"
                  value={gatewayToken}
                  data-emr-token
                  autoComplete="off"
                  onChange={(e) => setGatewayToken(e.currentTarget.value)}
                  placeholder={
                    credentialsExist
                      ? 'Leave blank to keep the existing token'
                      : 'Token (32+ chars)'
                  }
                />
                {credentialsExist ? (
                  <span className="text-xs text-muted-foreground">
                    A token is already stored. Submit a new value here to rotate it.
                  </span>
                ) : null}
              </Stack>
            </>
          ) : null}

          {upsert.error !== null && upsert.error !== undefined ? (
            <div className="error-banner text-sm" role="alert" aria-live="polite">
              {upsert.error.message}
            </div>
          ) : null}

          {upsert.isSuccess ? (
            <div role="status" aria-live="polite" className="text-sm text-muted-foreground">
              Connection saved.
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="submit" variant="primary" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save connection'}
            </Button>
            {current !== null && current !== undefined ? (
              <Button
                type="button"
                variant="danger"
                disabled={remove.isPending}
                data-emr-disconnect
                onClick={onDisconnect}
              >
                {remove.isPending ? 'Disconnecting…' : 'Disconnect'}
              </Button>
            ) : null}
          </div>
        </Stack>
      </form>
    </Card>
  );
}
